import { getRuntimeConfig, type LogLevel, type RuntimeConfig } from '@shared/config/runtime';

type TelemetryLevel = LogLevel;

interface TelemetryPayload {
  timestamp: string;
  level: TelemetryLevel;
  event: string;
  variant: RuntimeConfig['app']['variant'];
  version: string;
  environment: RuntimeConfig['app']['environment'];
  data?: Record<string, unknown>;
}

const LOG_LEVEL_ORDER: Record<TelemetryLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

let globalErrorTrackingInstalled = false;
let telemetryEndpointFailureLogged = false;

function shouldEmit(level: TelemetryLevel, minimumLevel: TelemetryLevel): boolean {
  return LOG_LEVEL_ORDER[level] >= LOG_LEVEL_ORDER[minimumLevel];
}

function serializeError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  return {
    message: String(error),
  };
}

function getConsoleMethod(level: TelemetryLevel): (...args: unknown[]) => void {
  if (level === 'error') {
    return console.error;
  }

  if (level === 'warn') {
    return console.warn;
  }

  if (level === 'debug') {
    return console.debug;
  }

  return console.info;
}

async function sendTelemetry(
  config: RuntimeConfig,
  payload: TelemetryPayload
): Promise<void> {
  if (!config.observability.telemetryEnabled || !config.observability.telemetryEndpoint) {
    return;
  }

  try {
    await fetch(config.observability.telemetryEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (error) {
    if (!telemetryEndpointFailureLogged) {
      telemetryEndpointFailureLogged = true;
      console.warn('[telemetry] Failed to send telemetry payload', serializeError(error));
    }
  }
}

function emitTelemetry(
  level: TelemetryLevel,
  event: string,
  data?: Record<string, unknown>
): void {
  const config = getRuntimeConfig();

  if (!shouldEmit(level, config.observability.logLevel)) {
    return;
  }

  const payload: TelemetryPayload = {
    timestamp: new Date().toISOString(),
    level,
    event,
    variant: config.app.variant,
    version: config.app.version,
    environment: config.app.environment,
    data,
  };

  const logPrefix = `[telemetry:${payload.variant}:${payload.version}] ${event}`;
  const consoleMethod = getConsoleMethod(level);
  consoleMethod(logPrefix, payload);

  void sendTelemetry(config, payload);
}

function installGlobalErrorTracking(): void {
  if (globalErrorTrackingInstalled || typeof window === 'undefined') {
    return;
  }

  window.addEventListener('error', (event) => {
    trackError('window.error', event.error ?? event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    trackError('window.unhandledrejection', event.reason);
  });

  globalErrorTrackingInstalled = true;
}

export function initializeObservability(): void {
  const config = getRuntimeConfig();
  installGlobalErrorTracking();

  trackEvent('runtime.initialized', {
    locale: config.app.locale,
    basePath: config.app.basePath,
    symfoniApiMode: config.symfoni.apiMode,
    symfoniDemoApiBase: config.symfoni.demoApiBase,
    proxyMode: config.network.corsMode,
    telemetryEnabled: config.observability.telemetryEnabled,
  });

  for (const warning of config.warnings) {
    trackWarn('runtime.warning', { message: warning });
  }
}

export function trackEvent(event: string, data?: Record<string, unknown>): void {
  emitTelemetry('info', event, data);
}

export function trackWarn(event: string, data?: Record<string, unknown>): void {
  emitTelemetry('warn', event, data);
}

export function trackError(
  event: string,
  error: unknown,
  data?: Record<string, unknown>
): void {
  emitTelemetry('error', event, {
    ...data,
    error: serializeError(error),
  });
}
